import { useEffect, useState, useMemo, useCallback, useRef, useId } from "react";
import { useProgramma } from "../ProgrammaShell";
import { portalQueries, readPortalCache, writePortalCache } from "@/client/lib/portal";
import { Button } from "@/client/components/ui/button";
import { Notice } from "@/client/components/ui/notice";
import { Checkbox } from "@/client/components/ui/checkbox";
import { cn } from "@/client/lib/utils";
import {
  Dialog,
  DialogTrigger,
  DialogPopup,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
  DialogPanel,
} from "@/client/components/ui/dialog";
import { CheckIcon, AlertCircleIcon, InfoIcon, PlusIcon, MinusIcon, TriangleAlert, CalendarIcon, ClockIcon, MapPinIcon, UsersIcon, ArrowRightIcon } from "lucide-react";
// SINGLE SOURCE OF TRUTH for eligibility + per-leerjaar vast exclusivity (see app/src/shared).
// The portal imports these via the @/ alias so the rules are identical on client and server.
import {
  isOfferingEligibleForGroup,
  eligibleYearLevelsForGroup,
  resolveVastClaim,
  isOfferingFullyAnswered,
  type OfferingRules,
  type GroupRules,
  type VastAssignment,
} from "@/shared/programme-rules";

type Offering = {
  id: number; programmeId: number; providerOrganizationId: number;
  enrolmentModeCode: string; decisionStyleCode: string; datePreferencePolicyCode: string;
  addonPolicyCode: string; selectionPrompt: string | null; requiredYearLevels: string[] | null; participatingYearLevels: string[] | null; maxYearLevels: string[] | null;
  vastStackable: boolean;
  // Part of the TechKwadraat scheme (3D printen, Lasersnijder in school). Does NOT affect
  // eligibility; it only drives the "eigen bijdrage" / TechKwadraat clarity (badge + info dialog).
  techKwadraat: boolean;
  // Admin-controlled display order from the Aanbod page (lower sorts first; ties fall back to id).
  // The /eligible route already returns offerings in this order; we re-apply it to the keuze blocks
  // so a school sees the keuze cards in the exact order the admin set, never an incidental order.
  sortOrder: number;
};
type OfferingYL = { offeringId: number; yearLevelCode: string };
export type Programme = {
  id: number; title: string; shortDescription: string | null; longDescription: string | null; imagePath: string | null;
  periodText: string | null; durationText: string | null; locationText: string | null;
  isNew: boolean; identitySensitive: boolean; oncePerYear: boolean; themaSpecifiek: boolean;
};
type Provider = { id: number; legalName: string; displayName: string | null };
type GroupYL = { groupId: number; displayName: string; yearLevelCode: string; studentCount: number };
type Selection = { id: number; offeringId: number; selectionOriginCode?: string };
type SelGroup = { selectionId: number; classGroupId: number };
type SelGroupYL = { selectionId: number; classGroupId: number; yearLevelCode: string };
type ChoiceSet = { id: number; code: string; title: string; minChoices: number; maxChoices: number };
type ChoiceSetMember = { choiceSetId: number; offeringId: number };
type Group = { id: number; displayName: string; yearLevels: { code: string; count: number }[] };
type Addon = { id: number; offeringId: number; addonCode: string; label: string; description: string | null; sortOrder: number };
// A row = the add-on is ANSWERED; `wanted` is the Ja (true) / Nee (false) answer. No row = unanswered.
// Per (selection, group): a selection spanning several groups answers add-ons separately per group.
type SelectionAddon = { selectionId: number; classGroupId: number; addonCode: string; wanted: boolean };
// Per (selection, group): each group of a selection has its own voorkeursperiode answer.
type TimePreference = { id: number; selectionId: number; classGroupId: number; preferenceKindCode: string; rankOrder: number | null; notes: string | null };
type TimePrefDateRange = { preferenceId: number; startDate: string; endDate: string };
type DateBlock = { id: number; offeringId: number; startDate: string; endDate: string; kind: string; label: string | null; sortOrder: number };

const ylNum = (code: string) => parseInt(code.replace("groep_", ""), 10) || 99;

// A voorkeursperiode is now EXPLICIT: its options come from the offering's own date-blocks, not a
// generic month list. Each block carries a {startDate,endDate}; the dropdown shows the block's label
// (or a formatted "start t/m eind" fallback when the label is null).
const MONTH_NAMES = ["januari", "februari", "maart", "april", "mei", "juni", "juli", "augustus", "september", "oktober", "november", "december"];
// Format a "YYYY-MM-DD" date string as e.g. "3 maart 2026" without going through Date (the column is
// already a plain date string, so we avoid timezone surprises).
function formatDate(iso: string): string {
  const [y, m, d] = iso.split("-");
  const mi = parseInt(m ?? "", 10) - 1;
  const month = MONTH_NAMES[mi] ?? m;
  return `${parseInt(d ?? "", 10)} ${month} ${y}`;
}
// The label shown for a date-block: its own label, else a "start t/m eind" (single date when the
// span is one day).
function blockLabel(b: DateBlock): string {
  if (b.label && b.label.trim()) return b.label.trim();
  return b.startDate === b.endDate ? formatDate(b.startDate) : `${formatDate(b.startDate)} t/m ${formatDate(b.endDate)}`;
}

export function VastKeuzeSection({ schoolId, mode = "vast_keuze" }: { schoolId: number; mode?: "vast_keuze" | "technohub" }) {
  const { setSaveState, setLastSavedAt, primaryAction, setSubNavItems, activeSubId, setActiveSubId } = useProgramma();

  const cachedElig = readPortalCache<any>(portalQueries.eligible(schoolId).queryKey);
  const cachedSel = readPortalCache<any>(portalQueries.selections(schoolId).queryKey);
  const [offerings, setOfferings] = useState<Offering[]>(() => cachedElig?.offerings ?? []);
  const [offeringYLs, setOfferingYLs] = useState<OfferingYL[]>(() => cachedElig?.offeringYearLevels ?? []);
  const [programmes, setProgrammes] = useState<Programme[]>(() => cachedElig?.programmes ?? []);
  const [providers, setProviders] = useState<Provider[]>(() => cachedElig?.providers ?? []);
  const [groupYLs, setGroupYLs] = useState<GroupYL[]>(() => cachedElig?.classGroupYearLevels ?? []);
  const [choiceSets, setChoiceSets] = useState<ChoiceSet[]>(() => cachedElig?.choiceSets ?? []);
  const [choiceSetMembers, setChoiceSetMembers] = useState<ChoiceSetMember[]>(() => cachedElig?.choiceSetMembers ?? []);
  const [blockedOfferingIds, setBlockedOfferingIds] = useState<number[]>(() => cachedElig?.blockedOfferingIds ?? []);
  const [addons, setAddons] = useState<Addon[]>(() => cachedElig?.offeringAddons ?? []);
  const [dateBlocks, setDateBlocks] = useState<DateBlock[]>(() => cachedElig?.offeringDateBlocks ?? []);
  const [selections, setSelections] = useState<Selection[]>(() => cachedSel?.selections ?? []);
  const [selGroups, setSelGroups] = useState<SelGroup[]>(() => cachedSel?.groups ?? []);
  const [selGroupYLs, setSelGroupYLs] = useState<SelGroupYL[]>(() => cachedSel?.groupYearLevels ?? []);
  const [optOuts, setOptOuts] = useState<{ classGroupId: number; enrolmentModeCode: string }[]>(() => cachedSel?.optOuts ?? []);
  const [timePrefs, setTimePrefs] = useState<TimePreference[]>(() => cachedSel?.timePreferences ?? []);
  const [timePrefRanges, setTimePrefRanges] = useState<TimePrefDateRange[]>(() => cachedSel?.timePreferenceDateRanges ?? []);
  const [selectionAddons, setSelectionAddons] = useState<SelectionAddon[]>(() => cachedSel?.selectionAddons ?? []);
  const [loading, setLoading] = useState(() => !cachedElig || !cachedSel);
  const [error, setError] = useState(false);

  // "Deze groep volgt geen vast programma" is a server-persisted opt-out (enrolment mode "vast"),
  // so KUVO sees it in the snapshot — see isGeenVast / toggleGeenVast below.

  // CONCURRENCY GUARD. Rapidly clicking several cards used to fire dozens of overlapping
  // save+reload cycles against stale render-closure state, saturating the main thread (the nav,
  // section steps and per-group sub-nav stopped responding) and leaving duplicate/ghost selections.
  // Two refs tame that:
  //  - savingRef: a single save is in flight at a time; a handler is a no-op while one runs (the
  //    controls are also disabled below, so this is the belt-and-braces backstop against a storm).
  //  - loadSeqRef: each load() is tagged with a monotonically increasing token, so a SUPERSEDED
  //    load (one started before a newer one) can never commit its (stale) result out of order.
  const savingRef = useRef(false);
  const loadSeqRef = useRef(0);

  const load = useCallback(async () => {
    const seq = ++loadSeqRef.current;
    // True only if THIS load is still the most recent one when its data arrives. A reload kicked
    // off by a newer save bumps the token, so an in-flight older reload silently drops its result.
    const isCurrent = () => seq === loadSeqRef.current;
    try {
      setError(false);
      const [selRes, eligRes] = await Promise.all([
        fetch(`/api/school/${schoolId}/selections`, { credentials: "include" }),
        fetch(`/api/school/${schoolId}/eligible`, { credentials: "include" }),
      ]);
      if (selRes.ok) {
        const d = await selRes.json();
        // Always refresh the shared cache (cheap, last-writer-wins is fine), but only push into
        // local React state when this load has not been superseded — otherwise a slow earlier
        // reload could clobber the fresh state a later save just installed.
        writePortalCache(portalQueries.selections(schoolId).queryKey, d);
        if (isCurrent()) {
          setSelections(d.selections ?? []); setSelGroups(d.groups ?? []); setSelGroupYLs(d.groupYearLevels ?? []); setOptOuts(d.optOuts ?? []);
          setTimePrefs(d.timePreferences ?? []); setTimePrefRanges(d.timePreferenceDateRanges ?? []); setSelectionAddons(d.selectionAddons ?? []);
        }
      }
      if (eligRes.ok) {
        const d = await eligRes.json();
        writePortalCache(portalQueries.eligible(schoolId).queryKey, d);
        if (isCurrent()) {
          setOfferings(d.offerings ?? []); setOfferingYLs(d.offeringYearLevels ?? []);
          setProgrammes(d.programmes ?? []); setProviders(d.providers ?? []); setGroupYLs(d.classGroupYearLevels ?? []);
          setChoiceSets(d.choiceSets ?? []); setChoiceSetMembers(d.choiceSetMembers ?? []); setBlockedOfferingIds(d.blockedOfferingIds ?? []);
          setAddons(d.offeringAddons ?? []); setDateBlocks(d.offeringDateBlocks ?? []);
        }
      }
      if ((!selRes.ok || !eligRes.ok) && isCurrent()) setError(true);
    } catch { if (isCurrent()) setError(true); }
    if (isCurrent()) setLoading(false);
  }, [schoolId]);

  // DEFAULT VAST ON is now assigned ONCE, server-side, when a group's leerjaren are established
  // (group create / year-level update — see services/vast-defaults.ts). We no longer materialise
  // defaults lazily on programma open, so an afgemelde vaste voorstelling STICKS after a refresh:
  // the selections snapshot already carries the (still default-on, or deliberately afgemelde) vaste.
  // So here we just load the eligible offerings + the current selections.
  useEffect(() => {
    void load(); // /eligible + /selections
  }, [load]);

  // After a selection saves: flag the toast. The parent /progress + stepper refresh is owned by
  // the shell's single saved-effect (it fires once on saveState === "saved" and coalesces rapid
  // saves), so we DON'T call refreshSections() here too — that double refresh (plus a full
  // invalidateSchool) was half the cascade that froze the nav. One settled save -> one refresh.
  const saved = () => {
    setSaveState("saved");
    setLastSavedAt(new Date().toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" }));
  };

  // Whether a save is currently running — drives the disabled/busy state on every card action so
  // the user physically can't fire a second save while one is mid-flight.
  const [saving, setSaving] = useState(false);

  // The ONE place a mutating handler runs. It (1) drops the click if a save is already in flight
  // (the ref is synchronous, so even same-tick double-clicks are caught), (2) flips saveState to
  // "saving" and disables the controls, (3) runs the handler's fetch(es), (4) reloads the fresh
  // snapshot, and (5) re-derives the next click from that fresh state — never the stale render
  // closure that produced ghosts/duplicates. `mutate` returns the request's ok (false -> error).
  const runSave = useCallback(async (mutate: () => Promise<boolean>) => {
    if (savingRef.current) return; // a save is already running: ignore the extra click
    savingRef.current = true;
    setSaving(true);
    setSaveState("saving");
    try {
      const ok = await mutate();
      if (ok) { await load(); saved(); } else setSaveState("error");
    } catch { setSaveState("error"); }
    finally { savingRef.current = false; setSaving(false); }
  }, [load]);

  const groups = useMemo<Group[]>(() => {
    const m = new Map<number, Group>();
    for (const r of groupYLs) {
      if (!m.has(r.groupId)) m.set(r.groupId, { id: r.groupId, displayName: r.displayName, yearLevels: [] });
      m.get(r.groupId)!.yearLevels.push({ code: r.yearLevelCode, count: r.studentCount });
    }
    for (const g of m.values()) g.yearLevels.sort((a, b) => ylNum(a.code) - ylNum(b.code));
    const rank = (g: Group) => Math.min(...g.yearLevels.map((y) => ylNum(y.code)));
    // Sort by the group's first (lowest) leerjaar, then a single-leerjaar groep before the
    // combigroepen that start with it (Groep 3 -> Combi 3/4 -> Combi 3/4/5), then by name.
    return [...m.values()].sort((a, b) => rank(a) - rank(b) || a.yearLevels.length - b.yearLevels.length || a.displayName.localeCompare(b.displayName, "nl", { numeric: true }));
  }, [groupYLs]);

  useEffect(() => {
    if (groups.length && (activeSubId === null || !groups.some((g) => g.id === activeSubId))) setActiveSubId(groups[0]!.id);
  }, [groups, activeSubId, setActiveSubId]);

  const programmeById = useMemo(() => new Map(programmes.map((p) => [p.id, p])), [programmes]);
  const providerById = useMemo(() => new Map(providers.map((p) => [p.id, p])), [providers]);
  const offeringById = useMemo(() => new Map(offerings.map((o) => [o.id, o])), [offerings]);
  const groupById = useMemo(() => new Map(groups.map((g) => [g.id, g])), [groups]);
  const blockedSet = useMemo(() => new Set(blockedOfferingIds), [blockedOfferingIds]);
  const modeSet = useMemo(() => new Set<string>(mode === "technohub" ? ["technohub"] : ["vast", "keuze"]), [mode]);
  const optOutMode = mode === "technohub" ? "technohub" : "keuze";
  const ylByOffering = useMemo(() => {
    const m = new Map<number, Set<string>>();
    for (const yl of offeringYLs) { if (!m.has(yl.offeringId)) m.set(yl.offeringId, new Set()); m.get(yl.offeringId)!.add(yl.yearLevelCode); }
    return m;
  }, [offeringYLs]);
  // The offering's year_levels as a plain array (the codes that make it eligible). Needed to build
  // the shared OfferingRules / call the shared eligibility + vast-exclusivity helpers.
  const offeringYLArray = useCallback((offeringId: number) => [...(ylByOffering.get(offeringId) ?? [])], [ylByOffering]);
  // Project a client Offering onto the shared OfferingRules shape (one place, used everywhere we
  // call into @/shared/programme-rules).
  const toRules = useCallback((o: Offering): OfferingRules => ({
    id: o.id,
    enrolmentModeCode: o.enrolmentModeCode,
    requiredYearLevels: o.requiredYearLevels,
    participatingYearLevels: o.participatingYearLevels,
    maxYearLevels: o.maxYearLevels,
    vastStackable: o.vastStackable,
  }), []);
  // Which leerjaren may TAKE PART once eligible (combi participation); falls back to year_levels.
  const participatingByOffering = useMemo(() => {
    const m = new Map<number, Set<string>>();
    for (const o of offerings) {
      const codes = o.participatingYearLevels?.length ? o.participatingYearLevels : [...(ylByOffering.get(o.id) ?? [])];
      m.set(o.id, new Set(codes));
    }
    return m;
  }, [offerings, ylByOffering]);
  const selectionByOffering = useMemo(() => new Map(selections.map((s) => [s.offeringId, s.id])), [selections]);
  // offeringId -> selection origin. A selection the system auto-assigned carries origin
  // 'auto_vast' / 'auto_keuze'; a deliberate school/admin choice is 'school_chosen' /
  // 'admin_assigned'. We surface the auto case as a subtle "Automatisch aangevinkt" label so
  // auto-selection is visible, never hidden. (A school re-pick always POSTs a fresh
  // 'school_chosen' selection, so an auto origin means the school has not changed it.)
  const originByOffering = useMemo(() => new Map(selections.map((s) => [s.offeringId, s.selectionOriginCode])), [selections]);
  const isAutoSelected = useCallback((offeringId: number) => {
    const o = originByOffering.get(offeringId);
    return o === "auto_vast" || o === "auto_keuze";
  }, [originByOffering]);

  // --- School-wide TechnoHub workshop count (drives the dynamic "Eigen bijdrage" badge) -------
  // A school may take MAX 2 TechnoHub-workshops free via KUVO; from the 3rd a school pays an eigen
  // bijdrage from the TechKwadraat-budget. The count is the TOTAL number of technohub bookings
  // across ALL groups school-wide (one per selection-group pair), not just the active group: every
  // selection on a technohub offering, times the groups assigned to it. Derived, never stored.
  const technohubOfferingIds = useMemo(
    () => new Set(offerings.filter((o) => o.enrolmentModeCode === "technohub").map((o) => o.id)),
    [offerings],
  );
  const technohubBookingCount = useMemo(() => {
    // selectionId -> is this a technohub selection? (its offering is a technohub offering)
    const technohubSelectionIds = new Set(
      selections.filter((s) => technohubOfferingIds.has(s.offeringId)).map((s) => s.id),
    );
    // One booking per (technohub selection, assigned group) pair, across the whole school.
    return selGroups.filter((sg) => technohubSelectionIds.has(sg.selectionId)).length;
  }, [selections, selGroups, technohubOfferingIds]);
  // Past the 2 free workshops -> the technohub cards carry an "Eigen bijdrage" badge.
  const technohubEigenBijdrage = technohubBookingCount > 2;

  const addonsByOffering = useMemo(() => {
    const m = new Map<number, Addon[]>();
    for (const a of [...addons].sort((x, y) => x.sortOrder - y.sortOrder)) { if (!m.has(a.offeringId)) m.set(a.offeringId, []); m.get(a.offeringId)!.push(a); }
    return m;
  }, [addons]);
  // `${selectionId}:${classGroupId}` -> (addonCode -> wanted). A present entry = the add-on is
  // ANSWERED (Ja=true/Nee=false) FOR THAT GROUP; an absent code = UNANSWERED for that group. Keyed per
  // (selection, group) so two groups doing the same activity each have their own Ja/Nee answers.
  const addonAnswersBySelGroup = useMemo(() => {
    const m = new Map<string, Map<string, boolean>>();
    for (const sa of selectionAddons) { const k = `${sa.selectionId}:${sa.classGroupId}`; if (!m.has(k)) m.set(k, new Map()); m.get(k)!.set(sa.addonCode, sa.wanted); }
    return m;
  }, [selectionAddons]);
  // The offering's own voorkeursperiode options (its date-blocks), ordered as the server sent them
  // (sortOrder then id). A voorkeursperiode is only offered when the offering has >=1 such block.
  const dateBlocksByOffering = useMemo(() => {
    const m = new Map<number, DateBlock[]>();
    for (const b of dateBlocks) { if (!m.has(b.offeringId)) m.set(b.offeringId, []); m.get(b.offeringId)!.push(b); }
    return m;
  }, [dateBlocks]);
  // The school's "preferred" periods per (selection, group) — voorkeur is MULTI: a group may pick ONE
  // OR MORE of the offering's date-blocks, each persisted as a ranked dated "preferred" time preference
  // (its picked date-block's {startDate,endDate}). A (selection, group) with no preferred range simply
  // has no voorkeur. Keyed `${selectionId}:${classGroupId}`, ranked by rankOrder (nulls last, id as
  // tiebreaker) for a stable order.
  const rangeByPref = useMemo(() => new Map(timePrefRanges.map((r) => [r.preferenceId, r])), [timePrefRanges]);
  const preferredRangesBySelGroup = useMemo(() => {
    const m = new Map<string, { startDate: string; endDate: string }[]>();
    const rows = timePrefs
      .filter((tp) => tp.preferenceKindCode === "preferred")
      .sort((a, b) => (a.rankOrder ?? 9999) - (b.rankOrder ?? 9999) || a.id - b.id);
    for (const tp of rows) {
      const r = rangeByPref.get(tp.id);
      if (!r) continue;
      const k = `${tp.selectionId}:${tp.classGroupId}`;
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push({ startDate: r.startDate, endDate: r.endDate });
    }
    return m;
  }, [timePrefs, rangeByPref]);
  // `${selectionId}:${classGroupId}` -> has the explicit "Geen voorkeur" marker (one geen_voorkeur
  // time preference, no date-range) FOR THAT GROUP. The school's deliberate "ik heb geen voorkeur"
  // answer; MUTUALLY EXCLUSIVE with picking periods (the /voorkeur save clears the one when the other
  // is set). Keyed per (selection, group).
  const geenVoorkeurBySelGroup = useMemo(() => {
    const s = new Set<string>();
    for (const tp of timePrefs) if (tp.preferenceKindCode === "geen_voorkeur") s.add(`${tp.selectionId}:${tp.classGroupId}`);
    return s;
  }, [timePrefs]);
  const groupsBySelection = useMemo(() => {
    const m = new Map<number, number[]>();
    for (const sg of selGroups) { if (!m.has(sg.selectionId)) m.set(sg.selectionId, []); m.get(sg.selectionId)!.push(sg.classGroupId); }
    return m;
  }, [selGroups]);
  const ylsBySelGroup = useMemo(() => {
    const m = new Map<string, Set<string>>();
    for (const r of selGroupYLs) { const k = `${r.selectionId}:${r.classGroupId}`; if (!m.has(k)) m.set(k, new Set()); m.get(k)!.add(r.yearLevelCode); }
    return m;
  }, [selGroupYLs]);

  // Eligible-and-participating leerjaren of g for an offering, via the SHARED rule (group leerjaren
  // ∩ the offering's participating codes, falling back to its year_levels). Single source of truth.
  const eligibleYLs = useCallback((offeringId: number, g: Group) => {
    const o = offeringById.get(offeringId);
    if (!o) return [];
    return eligibleYearLevelsForGroup(toRules(o), offeringYLArray(offeringId), { yearLevels: g.yearLevels } as GroupRules);
  }, [offeringById, toRules, offeringYLArray]);

  // Eligibility for a group is decided by the SHARED rule (content match AND required AND
  // exact-composition AND not-blocked); we additionally narrow to this section's modes (vast/keuze
  // or technohub). The combi rules live in @/shared/programme-rules now, not here.
  const eligibleOfferings = useCallback((g: Group) => {
    const codes = g.yearLevels.map((y) => y.code);
    return offerings.filter((o) =>
      modeSet.has(o.enrolmentModeCode)
      && isOfferingEligibleForGroup(toRules(o), offeringYLArray(o.id), codes, blockedSet.has(o.id)));
  }, [offerings, toRules, offeringYLArray, blockedSet, modeSet]);

  // Per-mode aanbod for a group. A dimension only needs resolving when the group has aanbod of that
  // mode. (In technohub mode there is no vast, so the vast dimension is always absent.) vastEligibleFor
  // is also THE set the GLOBAL per-leerjaar vast exclusivity ranges over (stackability included).
  const vastEligibleFor = useCallback((g: Group) => eligibleOfferings(g).filter((o) => o.enrolmentModeCode === "vast"), [eligibleOfferings]);
  const keuzeEligibleFor = useCallback((g: Group) => eligibleOfferings(g).filter((o) => o.enrolmentModeCode !== "vast"), [eligibleOfferings]);

  const isSelectedForGroup = useCallback((offeringId: number, groupId: number) => {
    const selId = selectionByOffering.get(offeringId);
    return !!selId && (groupsBySelection.get(selId) ?? []).includes(groupId);
  }, [selectionByOffering, groupsBySelection]);

  const selectedYLsFor = useCallback((offeringId: number, g: Group): string[] => {
    const selId = selectionByOffering.get(offeringId);
    if (!selId) return [];
    const stored = ylsBySelGroup.get(`${selId}:${g.id}`);
    if (stored && stored.size) return [...stored];
    return isSelectedForGroup(offeringId, g.id) ? eligibleYLs(offeringId, g) : [];
  }, [selectionByOffering, ylsBySelGroup, isSelectedForGroup, eligibleYLs]);

  // The full desired set of groups (with leerjaren) currently on an offering's selection.
  const currentGroups = useCallback((offeringId: number) => {
    const selId = selectionByOffering.get(offeringId);
    if (!selId) return [] as { classGroupId: number; yearLevels: string[] }[];
    return (groupsBySelection.get(selId) ?? []).map((gid) => {
      const g = groupById.get(gid);
      const stored = ylsBySelGroup.get(`${selId}:${gid}`);
      const yls = stored && stored.size ? [...stored] : (g ? eligibleYLs(offeringId, g) : []);
      return { classGroupId: gid, yearLevels: yls };
    });
  }, [selectionByOffering, groupsBySelection, ylsBySelGroup, groupById, eligibleYLs]);

  const saveSelectionGroups = useCallback(async (offeringId: number, desired: { classGroupId: number; yearLevels: string[] }[]) => {
    const selId = selectionByOffering.get(offeringId);
    const payload = desired.map((d) => {
      const g = groupById.get(d.classGroupId);
      return { classGroupId: d.classGroupId, includeFullGroup: !g || d.yearLevels.length >= g.yearLevels.length, yearLevels: d.yearLevels };
    });
    let res: Response;
    if (desired.length === 0) {
      if (!selId) return true;
      res = await fetch(`/api/school/${schoolId}/selections/${selId}`, { method: "DELETE", credentials: "include" });
    } else if (!selId) {
      res = await fetch(`/api/school/${schoolId}/selections`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ offeringId, selectionStatusCode: "interested", groups: payload }),
      });
    } else {
      // A PUT carrying `groups` makes the server replace ALL of the selection's children, which would
      // wipe the existing per-group voorkeursperioden. Resend the current time preferences, EACH with
      // its own classGroupId, so every group's voorkeur survives a leerjaren/group edit (a resent
      // preference for a group no longer in `payload` is simply not re-inserted). (Add-ons live on a
      // separate route and are untouched by this PUT.)
      const keptGroupIds = new Set(payload.map((p) => p.classGroupId));
      const tps = timePrefs
        .filter((tp) => tp.selectionId === selId && keptGroupIds.has(tp.classGroupId))
        .map((tp) => {
          const r = timePrefRanges.find((x) => x.preferenceId === tp.id);
          return {
            classGroupId: tp.classGroupId,
            preferenceKindCode: tp.preferenceKindCode,
            ...(tp.rankOrder != null ? { rankOrder: tp.rankOrder } : {}),
            ...(tp.notes ? { notes: tp.notes } : {}),
            ...(r ? { dateRange: { startDate: r.startDate, endDate: r.endDate } } : {}),
          };
        });
      res = await fetch(`/api/school/${schoolId}/selections/${selId}`, {
        method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ groups: payload, ...(tps.length ? { timePreferences: tps } : {}) }),
      });
    }
    return res.ok;
  }, [schoolId, selectionByOffering, groupById, timePrefs, timePrefRanges]);

  // Save the "preferred" voorkeursperiode(s) for ONE GROUP of an offering's selection via the
  // dedicated /voorkeur endpoint (replace-semantics scoped to (selection, classGroupId): it only
  // touches THAT GROUP'S preferred + geen_voorkeur rows, leaving groups/add-ons/avoid and OTHER groups'
  // voorkeur intact). Voorkeur is MULTI: `ranges` is the picked date-blocks' {startDate,endDate} (one
  // OR more), each stored as a ranked "preferred" period; an empty array means UNANSWERED again for
  // that group (no preferred rows, no geen_voorkeur marker). Picking a period also clears that group's
  // prior "Geen voorkeur" marker (the two are mutually exclusive).
  const saveVoorkeur = useCallback((offeringId: number, classGroupId: number, ranges: { startDate: string; endDate: string }[]) => {
    const selId = selectionByOffering.get(offeringId);
    if (!selId) return;
    void runSave(async () => {
      const res = await fetch(`/api/school/${schoolId}/selections/${selId}/voorkeur`, {
        method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ classGroupId, kind: "preferred", ranges }),
      });
      return res.ok;
    });
  }, [schoolId, selectionByOffering, runSave]);

  // Record the explicit "Geen voorkeur" answer for ONE GROUP of an offering's selection: one
  // geen_voorkeur marker for that group, clearing that group's picked periods (mutually exclusive).
  // This is a real, completeness-satisfying answer, not the absence of one. Uses the same dedicated
  // /voorkeur endpoint, scoped to (selection, classGroupId); other groups are untouched.
  const saveGeenVoorkeur = useCallback((offeringId: number, classGroupId: number) => {
    const selId = selectionByOffering.get(offeringId);
    if (!selId) return;
    void runSave(async () => {
      const res = await fetch(`/api/school/${schoolId}/selections/${selId}/voorkeur`, {
        method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ classGroupId, kind: "geen_voorkeur" }),
      });
      return res.ok;
    });
  }, [schoolId, selectionByOffering, runSave]);

  // Record the Ja/Nee answer for ONE add-on of ONE GROUP of an offering's selection. The /addons
  // endpoint is replace-semantics over the answers it receives FOR THAT GROUP (an add-on NOT listed
  // becomes unanswered for that group; other groups are untouched), so we resend EVERY add-on this
  // group has already answered plus the one being set, as { addonCode, wanted } answers. This keeps
  // the group's other Ja/Nee answers intact while updating just this one.
  const setAddonAnswer = useCallback((offeringId: number, classGroupId: number, addonCode: string, wanted: boolean) => {
    const selId = selectionByOffering.get(offeringId);
    if (!selId) return;
    const cur = addonAnswersBySelGroup.get(`${selId}:${classGroupId}`) ?? new Map<string, boolean>();
    const merged = new Map(cur);
    merged.set(addonCode, wanted);
    const answers = [...merged].map(([code, w]) => ({ addonCode: code, wanted: w }));
    void runSave(async () => {
      const res = await fetch(`/api/school/${schoolId}/selections/${selId}/addons`, {
        method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ classGroupId, answers }),
      });
      return res.ok;
    });
  }, [schoolId, selectionByOffering, addonAnswersBySelGroup, runSave]);

  // Picking a vaste voorstelling for a group makes the "geen vast" decision moot, so clear it.
  const clearGeenVastFor = useCallback(async (groupId: number) => {
    if (!optOuts.some((o) => o.classGroupId === groupId && o.enrolmentModeCode === "vast")) return;
    const next = optOuts.filter((o) => o.enrolmentModeCode === "vast").map((o) => o.classGroupId).filter((id) => id !== groupId);
    await fetch(`/api/school/${schoolId}/optouts/vast`, { method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify({ classGroupIds: next }) });
  }, [optOuts, schoolId]);

  // Add/remove a group on an offering's selection. Used for BOTH vast and keuze: each offering is
  // its own independent confirm/decline now (per-choice-set "kies 1" exclusivity is handled by
  // pickChoiceMember). Vaste shows that are NOT in a choice set may therefore stack freely.
  const toggle = useCallback((offeringId: number, g: Group) => {
    const selected = isSelectedForGroup(offeringId, g.id);
    const isVast = offeringById.get(offeringId)?.enrolmentModeCode === "vast";
    void runSave(async () => {
      const cur = currentGroups(offeringId);
      const next = selected ? cur.filter((d) => d.classGroupId !== g.id) : [...cur, { classGroupId: g.id, yearLevels: eligibleYLs(offeringId, g) }];
      const ok = await saveSelectionGroups(offeringId, next);
      if (ok && !selected && isVast) await clearGeenVastFor(g.id);
      return ok;
    });
  }, [isSelectedForGroup, offeringById, currentGroups, eligibleYLs, saveSelectionGroups, clearGeenVastFor, runSave]);

  const setLeerjaren = useCallback((offeringId: number, g: Group, yls: string[]) => {
    void runSave(async () => {
      const cur = currentGroups(offeringId);
      const next = yls.length === 0
        ? cur.filter((d) => d.classGroupId !== g.id)
        : cur.some((d) => d.classGroupId === g.id)
          ? cur.map((d) => (d.classGroupId === g.id ? { ...d, yearLevels: yls } : d))
          : [...cur, { classGroupId: g.id, yearLevels: yls }];
      return saveSelectionGroups(offeringId, next);
    });
  }, [currentGroups, saveSelectionGroups, runSave]);

  // KEUZE afmelden: FULLY deselect this group from the offering. We drop the group entirely from
  // the selection's desired set (NOT merely empty its leerjaren), so saveSelectionGroups DELETEs
  // the school_offering_selection when this was the only group and otherwise PUTs the remaining
  // groups. This is what makes a keuze afmelding STICK after a refresh: no "selected-but-no-
  // leerjaren" group row is ever left behind to reload as still-selected. Used by both the standalone
  // keuze deselect AND the combigroup leerjaar-selector "Afmelden". (Vaste afmelden has its own
  // exclusivity path via applyVastClaim with an empty claim.)
  const deselectKeuze = useCallback((offeringId: number, g: Group) => {
    void runSave(async () => {
      const next = currentGroups(offeringId).filter((d) => d.classGroupId !== g.id);
      return saveSelectionGroups(offeringId, next);
    });
  }, [currentGroups, saveSelectionGroups, runSave]);

  const pickChoiceMember = useCallback((memberIds: number[], chosenId: number | null, g: Group) => {
    void runSave(async () => {
      let ok = true;
      for (const offId of memberIds) {
        const want = offId === chosenId;
        if (want === isSelectedForGroup(offId, g.id)) continue;
        const cur = currentGroups(offId);
        const next = want ? [...cur, { classGroupId: g.id, yearLevels: eligibleYLs(offId, g) }] : cur.filter((d) => d.classGroupId !== g.id);
        ok = (await saveSelectionGroups(offId, next)) && ok;
      }
      // Choosing a vaste voorstelling out of a "kies 1" vast set clears the "geen vast" flag.
      if (ok && chosenId != null && offeringById.get(chosenId)?.enrolmentModeCode === "vast") await clearGeenVastFor(g.id);
      return ok;
    });
  }, [isSelectedForGroup, currentGroups, eligibleYLs, saveSelectionGroups, offeringById, clearGeenVastFor, runSave]);

  // --- GLOBAL per-leerjaar vast exclusivity ---------------------------------------------------
  // Each leerjaar of group G belongs to at most ONE non-stackable vaste, across ALL of G's eligible
  // vaste (no longer scoped to a choice set). Stackable vaste are exempt and always coexist. We snap
  // the CURRENT assignment (which vaste hold which leerjaren of G), let the SHARED resolveVastClaim
  // compute the new assignment, then persist only the offerings whose leerjaren actually changed.
  //
  // claimerId   = the vaste voorstelling whose leerjaren we are setting
  // claimedYLs  = G's leerjaren the claimer now owns (already filtered to eligible/desired)
  const applyVastClaim = useCallback((claimerId: number, claimedYLs: string[], g: Group) => {
    void runSave(async () => {
      const eligibleVaste = vastEligibleFor(g);
      const rules = eligibleVaste.map((o) => toRules(o));
      // Snapshot: each eligible vaste -> the leerjaren of G it currently owns (omit the unselected).
      const current: VastAssignment = new Map();
      for (const o of eligibleVaste) {
        const yls = selectedYLsFor(o.id, g);
        if (yls.length) current.set(o.id, yls);
      }
      const next = resolveVastClaim(current, { yearLevels: g.yearLevels } as GroupRules, claimerId, claimedYLs, rules);

      // Persist every eligible vaste whose owned leerjaren changed (added, removed, or emptied).
      let ok = true;
      const sameYLs = (a: string[], b: string[]) => a.length === b.length && [...a].sort().join() === [...b].sort().join();
      for (const o of eligibleVaste) {
        const before = current.get(o.id) ?? [];
        const after = next.get(o.id) ?? [];
        if (sameYLs(before, after)) continue;
        const cur = currentGroups(o.id);
        const others = cur.filter((d) => d.classGroupId !== g.id);
        const desired = after.length === 0 ? others : [...others, { classGroupId: g.id, yearLevels: after }];
        ok = (await saveSelectionGroups(o.id, desired)) && ok;
      }
      if (ok && claimedYLs.length > 0 && offeringById.get(claimerId)?.enrolmentModeCode === "vast") await clearGeenVastFor(g.id);
      return ok;
    });
  }, [vastEligibleFor, toRules, selectedYLsFor, currentGroups, saveSelectionGroups, offeringById, clearGeenVastFor, runSave]);

  // Click a vaste voorstelling: it claims ALL of G's leerjaren eligible for it, and (when it is not
  // stackable) those leerjaren are stripped from the OTHER non-stackable vaste of the group.
  const pickVastMember = useCallback((chosenId: number, g: Group) =>
    applyVastClaim(chosenId, eligibleYLs(chosenId, g), g), [applyVastClaim, eligibleYLs]);

  // Per-offering "welke leerjaren doen mee?" on a selected vaste: the offering now owns exactly
  // `yls`; toggling a leerjaar ON claims it (stripping it from non-stackable peers), OFF removes it
  // (deselect when empty). Same GLOBAL exclusivity path so each eligible leerjaar stays uniquely owned.
  const setVastLeerjaren = useCallback((offeringId: number, g: Group, yls: string[]) =>
    applyVastClaim(offeringId, yls, g), [applyVastClaim]);

  // Whether a SELECTED offering is FULLY ANSWERED FOR ONE GROUP, via the SHARED rule
  // (isOfferingFullyAnswered) so this section and the Controleren step agree to the letter: for THAT
  // GROUP, an offering with voorkeursperiode date-blocks needs >=1 chosen period OR an explicit "Geen
  // voorkeur", and an offering with add-ons needs EVERY add-on answered (Ja/Nee). Offerings that ask
  // for neither are trivially answered. (An offering that is NOT selected for the group has nothing to
  // answer.) Voorkeur + add-ons are now PER (selection, group), so this reads that group's answers.
  // Applies to vast AND keuze.
  const offeringFullyAnswered = useCallback((offeringId: number, groupId: number) => {
    const selId = selectionByOffering.get(offeringId);
    if (!selId) return true;
    const key = `${selId}:${groupId}`;
    return isOfferingFullyAnswered(
      {
        hasDateBlocks: (dateBlocksByOffering.get(offeringId)?.length ?? 0) > 0,
        addonCodes: (addonsByOffering.get(offeringId) ?? []).map((a) => a.addonCode),
      },
      {
        hasPreferredPeriod: (preferredRangesBySelGroup.get(key)?.length ?? 0) > 0,
        hasGeenVoorkeur: geenVoorkeurBySelGroup.has(key),
        answeredAddonCodes: new Set(addonAnswersBySelGroup.get(key)?.keys() ?? []),
      },
    );
  }, [selectionByOffering, dateBlocksByOffering, addonsByOffering, preferredRangesBySelGroup, geenVoorkeurBySelGroup, addonAnswersBySelGroup]);

  const isSectionOptedOut = useCallback((groupId: number) => optOuts.some((o) => o.classGroupId === groupId && o.enrolmentModeCode === optOutMode), [optOuts, optOutMode]);
  const toggleSectionOptOut = useCallback((groupId: number) => {
    void runSave(async () => {
      const cur = optOuts.filter((o) => o.enrolmentModeCode === optOutMode).map((o) => o.classGroupId);
      const next = cur.includes(groupId) ? cur.filter((id) => id !== groupId) : [...cur, groupId];
      const res = await fetch(`/api/school/${schoolId}/optouts/${optOutMode}`, {
        method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ classGroupIds: next }),
      });
      return res.ok;
    });
  }, [optOuts, optOutMode, schoolId, runSave]);

  const isGeenVast = useCallback((groupId: number) => optOuts.some((o) => o.classGroupId === groupId && o.enrolmentModeCode === "vast"), [optOuts]);
  const toggleGeenVast = useCallback((groupId: number) => {
    void runSave(async () => {
      const cur = optOuts.filter((o) => o.enrolmentModeCode === "vast").map((o) => o.classGroupId);
      const next = cur.includes(groupId) ? cur.filter((id) => id !== groupId) : [...cur, groupId];
      const res = await fetch(`/api/school/${schoolId}/optouts/vast`, { method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify({ classGroupIds: next }) });
      return res.ok;
    });
  }, [optOuts, schoolId, runSave]);

  // Any offering SELECTED for this group whose voorkeursperiode and/or add-ons are NOT yet fully
  // answered (via the shared offeringFullyAnswered). Applies to vast AND keuze, so an auto-selected
  // group (e.g. Groep 3/4 with 1 vast + 1 keuze) stays unresolved until the school gives a
  // voorkeursperiode (a period OR "Geen voorkeur") and answers every add-on (Ja/Nee). This is the
  // SAME rule the Controleren step uses (computeProgrammeCompleteness), so the two cannot drift.
  const answersMissing = useCallback((g: Group) =>
    eligibleOfferings(g).some((o) => isSelectedForGroup(o.id, g.id) && !offeringFullyAnswered(o.id, g.id)),
    [eligibleOfferings, isSelectedForGroup, offeringFullyAnswered]);

  const vastDecided = useCallback((g: Group) => isGeenVast(g.id) || vastEligibleFor(g).some((o) => isSelectedForGroup(o.id, g.id)), [isGeenVast, vastEligibleFor, isSelectedForGroup]);
  const keuzeDecided = useCallback((g: Group) => isSectionOptedOut(g.id) || keuzeEligibleFor(g).some((o) => isSelectedForGroup(o.id, g.id)), [isSectionOptedOut, keuzeEligibleFor, isSelectedForGroup]);

  // A group is resolved when BOTH its vast decision (≥1 vast OR "geen vast") and its keuze
  // decision (≥1 keuze OR keuze-opt-out) are made — but only for the dimensions that actually
  // have aanbod for this group. Groups with no aanbod at all are auto-resolved. On top of that,
  // EVERY selected offering must be fully answered (voorkeursperiode + add-ons, see answersMissing),
  // otherwise the group stays unresolved (matches the shared completeness rule one-to-one).
  const groupHasChoice = useCallback((g: Group) => {
    const vastOk = vastEligibleFor(g).length === 0 || vastDecided(g);
    const keuzeOk = keuzeEligibleFor(g).length === 0 || keuzeDecided(g);
    return vastOk && keuzeOk && !answersMissing(g);
  }, [vastEligibleFor, keuzeEligibleFor, vastDecided, keuzeDecided, answersMissing]);

  // A group is "started" (deels ingevuld) when it has SOME decision made on either dimension
  // (≥1 vast selected OR "geen vast", OR ≥1 keuze selected OR a keuze-opt-out). The sub-nav and
  // the rolled-up parent icon use this (when not yet fully resolved) to show the (o) dot instead of
  // a blank ring, so a half-filled group reads as in-progress before the first aanlevering.
  const groupStarted = useCallback((g: Group) => vastDecided(g) || keuzeDecided(g), [vastDecided, keuzeDecided]);

  // Publish the groups (with per-group resolved + partial status) so the shell renders them as the
  // nested sub-nav under this section in the left stepper. `partial` = started but NOT yet resolved;
  // the shell maps resolved -> green/blue, partial -> (o) dot, nothing -> ring, and rolls partial up
  // to the parent section icon.
  useEffect(() => {
    setSubNavItems(groups.map((g) => {
      const resolved = groupHasChoice(g);
      return { id: g.id, label: g.displayName, resolved, partial: !resolved && groupStarted(g) };
    }));
  }, [groups, groupHasChoice, groupStarted, setSubNavItems]);

  if (loading) return <p className="text-sm text-muted-foreground">Laden...</p>;
  if (error) return (
    <Notice tone="error" icon={<AlertCircleIcon />} title="Kan het programma niet laden"
      action={<Button size="sm" variant="outline" onClick={() => { setLoading(true); load(); }}>Opnieuw proberen</Button>}>
      Controleer uw internetverbinding en probeer het opnieuw.
    </Notice>
  );
  if (groups.length === 0) return (
    <Notice tone="info" icon={<AlertCircleIcon />} title="Nog geen groepen">
      Voeg eerst groepen toe bij Gegevens; daarna kiest u hier per groep het programma.
    </Notice>
  );

  const activeGroup = groups.find((g) => g.id === activeSubId) ?? groups[0]!;
  const isCombi = activeGroup.yearLevels.length >= 2;
  const eligible = eligibleOfferings(activeGroup);
  const eligibleIds = new Set(eligible.map((o) => o.id));
  // ALL eligible vaste of this group, presented TOGETHER in one "Vast programma" block. The
  // per-leerjaar exclusivity is GLOBAL across this whole set (applyVastClaim / pickVastMember),
  // so choice-set membership is no longer a visual unit for vaste: every vaste card renders via
  // renderCard on the global vast path, regardless of whether it shares a choice set with others.
  const vast = vastEligibleFor(activeGroup);
  // KEUZE choice sets bound to this group: "kies 1" groups. Only sets where NO member is vast are
  // keuze sets (a set with any vaste member is no longer a visual unit; its vaste fold into the
  // unified vast block above, and a "kies 1" box for vaste no longer exists).
  const keuzeSets = choiceSets
    .map((cs) => ({ cs, memberIds: choiceSetMembers.filter((m) => m.choiceSetId === cs.id).map((m) => m.offeringId).filter((id) => eligibleIds.has(id)) }))
    .filter((x) => x.memberIds.length >= 2 && !x.memberIds.some((id) => offeringById.get(id)?.enrolmentModeCode === "vast"));
  const keuzeSetMemberIds = new Set(keuzeSets.flatMap((x) => x.memberIds));
  // Standalone keuze cards: every non-vast eligible offering that is NOT in a keuze "kies 1" set.
  // Matches keuzeEligibleFor so what renders here equals what completeness expects. Explicitly
  // ordered by the admin-controlled sortOrder (id as the stable tiebreaker), so the keuze cards
  // appear in the same order as the admin Aanbod page. (/eligible already returns offerings in this
  // order; we re-sort here so the order is guaranteed regardless of any upstream array shuffling.)
  const keuze = eligible
    .filter((o) => o.enrolmentModeCode !== "vast" && !keuzeSetMemberIds.has(o.id))
    .sort((a, b) => a.sortOrder - b.sortOrder || a.id - b.id);
  // Whether any eligible vaste of this group may coexist (stackable): drives a soft "sommige mogen
  // samen" note in the vast block, shown only when relevant.
  const anyVastStackable = vast.some((o) => o.vastStackable);
  const hasVastAanbod = vast.length > 0;
  const hasKeuzeAanbod = keuze.length > 0 || keuzeSets.length > 0;
  const ylLabel = activeGroup.yearLevels.map((y) => `leerjaar ${ylNum(y.code)}`).join(", ");

  // Completeness + the bevestiging gate now live in the Controleren step, not here. This section
  // only collects per-group choices; groupHasChoice (above) still feeds the per-group sub-nav icons.
  // The keuze "doet niet mee" opt-out is only relevant while the group has not chosen any keuze;
  // once a keuze is picked it is contradictory, so hide it (unless actually opted out).
  const anyKeuzeChosen = keuze.some((o) => isSelectedForGroup(o.id, activeGroup.id)) || keuzeSets.some((x) => x.memberIds.some((id) => isSelectedForGroup(id, activeGroup.id)));
  // The vast "geen vast programma" option mirrors that: hidden once a vaste voorstelling is chosen.
  const anyVastChosen = vast.some((o) => isSelectedForGroup(o.id, activeGroup.id));

  const badgesFor = (p: Programme) => [p.isNew && "NIEUW", p.identitySensitive && "Let op identiteit", p.themaSpecifiek && "Groep 5 óf 6"].filter(Boolean) as string[];

  // Combi leerjaar selector: which leerjaren of this group take part; ineligible ones greyed out.
  // For a vaste voorstelling, toggling routes through the GLOBAL setVastLeerjaren so a claimed
  // leerjaar is stripped from the non-stackable peers; keuze uses plain setLeerjaren. MINIMAL now:
  // just the prompt + per-leerjaar chips. The card's one "- Afmelden" toggle button owns the full
  // afmelding, and its title row owns the "Automatisch aangevinkt" marker, so this control needs
  // neither. Emptying the LAST leerjaar of a keuze still FULLY deselects (so no zombie row is left).
  const renderLeerjaarSelector = (o: Offering, isVastCard: boolean) => {
    if (!isCombi) return null;
    const offCodes = participatingByOffering.get(o.id) ?? new Set();
    const sel = new Set(selectedYLsFor(o.id, activeGroup));
    // Emptying a keuze (toggling off the last leerjaar) is a FULL deselect, so the group row is
    // removed (and the selection deleted when it was the only group) and the afmelding sticks after
    // a refresh — never a "selected-but-no-leerjaren" zombie. Vaste keep their own GLOBAL
    // exclusivity path (setVastLeerjaren), which already deselects on empty.
    const apply = (next: string[]) =>
      isVastCard
        ? setVastLeerjaren(o.id, activeGroup, next)
        : next.length === 0
          ? deselectKeuze(o.id, activeGroup)
          : setLeerjaren(o.id, activeGroup, next);
    return (
      <div className="mt-2 rounded-md border border-primary/30 bg-primary/[0.04] px-3 py-2.5">
        <p className="text-xs font-semibold text-foreground">Welke leerjaren van deze groep doen mee?</p>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {activeGroup.yearLevels.map((y) => {
            const ok = offCodes.has(y.code);
            const on = sel.has(y.code);
            if (!ok) return <span key={y.code} className="rounded-full border border-dashed border-border px-2.5 py-1 text-xs text-muted-foreground/60">leerjaar {ylNum(y.code)} · niet mogelijk</span>;
            return (
              <button key={y.code} type="button" disabled={saving}
                onClick={() => apply(on ? [...sel].filter((c) => c !== y.code) : [...sel, y.code])}
                className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors disabled:pointer-events-none disabled:opacity-50 ${on ? "border-primary bg-primary/10 text-primary" : "border-input bg-background text-foreground hover:border-primary/40"}`}>
                {on && <CheckIcon size={12} />}leerjaar {ylNum(y.code)}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  // choiceCtx (KEUZE sets only) carries the set's member ids for the naive "kies 1" pick-1. Vaste
  // voorstellingen never use a pick-1 ctx now: their per-leerjaar exclusivity is GLOBAL across the
  // group's eligible vaste, so a vast card always routes through pickVastMember + setVastLeerjaren.
  //
  // COMPACT card. The verbose all-info card was too cluttered, so the card now carries only what the
  // school needs to DECIDE and SET: a small image (omitted cleanly when none), the title + vast/keuze
  // badge + provider, a "Meer informatie" link to the read-only details dialog, ONE toggle action
  // button (+ Aanmelden / - Afmelden, autosave), and, once selected, the COMPACT controls (leerjaar
  // selector on a combigroep, voorkeursperiode when there are date-blocks, add-ons when there are any).
  // Wanneer / Duur / Locatie / Voor leerjaren and the description live ONLY in the details dialog.
  const renderCard = (o: Offering, choiceCtx?: { memberIds: number[] }) => {
    const p = programmeById.get(o.programmeId);
    if (!p) return null;
    const prov = providerById.get(o.providerOrganizationId);
    const selected = isSelectedForGroup(o.id, activeGroup.id);
    const isVast = o.enrolmentModeCode === "vast";
    const badges = badgesFor(p);
    const badgeLabel = isVast ? "Vast aanbod" : mode === "technohub" ? "Workshop" : "Keuze";
    const onPrimary = () => (isVast ? pickVastMember(o.id, activeGroup) : choiceCtx ? pickChoiceMember(choiceCtx.memberIds, o.id, activeGroup) : toggle(o.id, activeGroup));
    // Vaste cards deselect per-leerjaar (claim none) via their GLOBAL exclusivity path. KEUZE
    // afmelden FULLY deselects: a "kies 1" member clears via pickChoiceMember(null) (which removes
    // the group), and a standalone keuze removes the group via deselectKeuze. Both delete the
    // school_offering_selection-group for this group so the afmelding sticks after a refresh.
    const onClear = () => (isVast ? setVastLeerjaren(o.id, activeGroup, []) : choiceCtx ? pickChoiceMember(choiceCtx.memberIds, null, activeGroup) : deselectKeuze(o.id, activeGroup));
    const isTechnohub = mode === "technohub";
    const autoSelected = selected && isAutoSelected(o.id);
    // COMPACT card image: a 1:1 square on the LEFT, object-cover, card radius, top-aligned. A touch
    // larger now (size-32) so it reads as a real thumbnail while the card stays compact. technohub
    // source images carry a white background, so they go object-contain. No image => the image is
    // OMITTED entirely (no empty/broken box), so image-less cards stay tidy.
    const img = p.imagePath
      ? <img src={p.imagePath} alt="" className={cn("size-32 shrink-0 rounded-lg", isTechnohub ? "object-contain" : "object-cover")} loading="lazy" />
      : null;
    return (
      // BORDERLESS card: no outer bordered box. Cards are separated by the surrounding spacing (and a
      // subtle divider in the list). SELECTED is a subtle background tint plus the toggle button's
      // "- Afmelden" state, keeping the section clean.
      <article key={o.id} className={cn("overflow-hidden rounded-xl transition-colors", selected && "bg-primary/[0.04]")}>
        <div className="flex items-start gap-3 p-3">
          {img}
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            {/* Title row: image top-aligns with this row. Name + badges on the left, "Meer
                informatie" pinned top-right so the card stays compact. */}
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <h3 className="text-[15px] font-semibold leading-tight text-foreground">{p.title}</h3>
                  <span className={cn("rounded px-1.5 py-0.5 text-[11px] font-semibold", isVast ? "bg-amber-100 text-amber-800" : "bg-primary/10 text-primary")}>{badgeLabel}</span>
                  {/* The "Mag samen" badge was unclear; stackability is now shown as a clear neutral
                      info note on the card (below), not a cryptic badge. */}
                  {badges.map((b) => <span key={b} className="rounded bg-muted px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground">{b}</span>)}
                  {autoSelected && (
                    <span className="text-[11px] font-medium text-muted-foreground" title="KUVO heeft dit automatisch aangemeld. U kunt het aanpassen of afmelden.">Automatisch aangemeld</span>
                  )}
                </div>
                {prov && <p className="mt-0.5 text-xs text-muted-foreground">{prov.displayName || prov.legalName}</p>}
              </div>
              <OfferingDetailsDialog
                programme={p}
                providerName={prov ? prov.displayName || prov.legalName : null}
                voorLeerjaren={isCombi
                  ? (activeGroup.yearLevels.filter((y) => (participatingByOffering.get(o.id) ?? new Set()).has(y.code)).map((y) => `leerjaar ${ylNum(y.code)}`).join(", ") || null)
                  : null}
                voorkeursperioden={(dateBlocksByOffering.get(o.id) ?? []).map((b) => b.label).filter((l): l is string => !!l)}
                addons={(addonsByOffering.get(o.id) ?? []).map((a) => a.label)}
                trigger={
                  <button type="button" className="shrink-0 cursor-pointer text-xs font-medium text-primary underline-offset-2 hover:underline">
                    Meer informatie
                  </button>
                }
              />
            </div>

            {/* SHORT description on the compact card: a 2-line teaser under the title/provider. The
                FULL (long) description lives only in the "Meer informatie" dialog, so the card stays
                compact. Omitted cleanly when the activity has no short description. */}
            {p.shortDescription && (
              <p className="line-clamp-2 text-xs leading-5 text-muted-foreground">{p.shortDescription}</p>
            )}

            {/* Stackable vaste voorstelling: a clear neutral info note (replaces the cryptic "Mag
                samen" badge) so the school understands this group may keep this AND another vaste. */}
            {isVast && o.vastStackable && (
              <div className="rounded-md border px-4 py-3">
                <p className="text-sm text-muted-foreground">
                  <InfoIcon aria-hidden className="-mt-0.5 me-2.5 inline-flex text-blue-500" size={15} />
                  Deze vaste voorstelling kan samen met een andere vaste voorstelling worden gekozen.
                </p>
              </div>
            )}

            {/* TechKwadraat clarity, as a clean WARN alert ON the card (not a small badge).
                In the technohub section it appears DYNAMICALLY once the school passes its 2 free
                workshops school-wide; outside it (vast_keuze) it is STATIC on offerings flagged
                techKwadraat (3D printen, Lasersnijder in school). Both make clear the activity loopt
                via TechKwadraat met een eigen bijdrage, and link to the shared info dialog. */}
            {(isTechnohub ? technohubEigenBijdrage : o.techKwadraat) && <TechKwadraatNotice />}
            {/* The "Toelichting bij de keuze" (admin freetext per offering) is GUIDANCE, not a warning,
                so it reads as a calm neutral info note (blue InfoIcon), not an alarming amber alert. */}
            {o.selectionPrompt && (
              <div className="rounded-md border px-4 py-3">
                <p className="text-sm text-muted-foreground">
                  <InfoIcon aria-hidden className="-mt-0.5 me-2.5 inline-flex text-blue-500" size={15} />
                  {o.selectionPrompt}
                </p>
              </div>
            )}

            {/* ONE toggle action button (autosave). + Aanmelden when not selected, - Afmelden when
                selected. Disabled while a save is in flight so a rapid second click cannot queue a
                duplicate selection. */}
            <div className="mt-0.5">
              {selected ? (
                <Button size="sm" variant="outline" onClick={onClear} disabled={saving} className="text-muted-foreground hover:text-foreground">
                  <MinusIcon />
                  Afmelden
                </Button>
              ) : (
                <Button size="sm" onClick={onPrimary} disabled={saving}>
                  <PlusIcon />
                  Aanmelden
                </Button>
              )}
            </div>

            {selected && renderLeerjaarSelector(o, isVast)}
            {selected && renderVoorkeur(o)}
            {selected && renderAddons(o)}
          </div>
        </div>
      </article>
    );
  };

  // Voorkeursperiode: EXPLICIT, never automatic, and MULTI. Shown on a SELECTED offering ONLY when
  // that offering actually has >=1 date-block; the options ARE those blocks (label, or a "start t/m
  // eind" fallback), each mapping to the block's {startDate,endDate}. A school must answer it: pick
  // ONE OR MORE periods (each persisted as a ranked "preferred" period) OR tick "Geen voorkeur" (a
  // geen_voorkeur marker). The two are MUTUALLY EXCLUSIVE. It is ALWAYS required now (an answer folds
  // into the group's completeness via offeringFullyAnswered), so the amber hint shows until either is
  // chosen.
  const renderVoorkeur = (o: Offering) => {
    const blocks = dateBlocksByOffering.get(o.id);
    if (!blocks || blocks.length === 0) return null;
    const selId = selectionByOffering.get(o.id);
    if (!selId) return null;
    // Voorkeur is PER (selection, group): read + write the ACTIVE GROUP's answer only.
    const gid = activeGroup.id;
    const key = `${selId}:${gid}`;
    const cur = preferredRangesBySelGroup.get(key) ?? [];
    const geenVoorkeur = geenVoorkeurBySelGroup.has(key);
    // Match each stored range back to one of the offering's blocks (by start+end) -> chosen block ids.
    const chosenIds = cur
      .map((r) => blocks.find((b) => b.startDate === r.startDate && b.endDate === r.endDate)?.id)
      .filter((id): id is number => id != null)
      .map((id) => String(id));
    // Persist in the offering's own block ORDER, restricted to the toggled set, so the saved
    // rankOrder mirrors the displayed grid order rather than the click order.
    const persist = (nextIds: string[]) => {
      const set = new Set(nextIds);
      const ranges = blocks
        .filter((b) => set.has(String(b.id)))
        .map((b) => ({ startDate: b.startDate, endDate: b.endDate }));
      saveVoorkeur(o.id, gid, ranges);
    };
    return (
      // Key on (selection, group) so switching the active group remounts with that group's answer.
      <VoorkeurControl
        key={`vk-${key}`}
        disabled={saving}
        value={chosenIds}
        geenVoorkeur={geenVoorkeur}
        options={blocks.map((b) => ({ value: String(b.id), label: blockLabel(b) }))}
        onToggle={(blockId) => {
          // Picking a period clears "Geen voorkeur" (the /voorkeur save does this server-side too).
          const set = new Set(geenVoorkeur ? [] : chosenIds);
          if (set.has(blockId)) set.delete(blockId); else set.add(blockId);
          persist([...set]);
        }}
        onGeenVoorkeur={() => {
          // Ticking "Geen voorkeur" while already on it goes back to UNANSWERED (clears everything);
          // otherwise it deselects all periods and records the marker. Scoped to the active group.
          if (geenVoorkeur) saveVoorkeur(o.id, gid, []);
          else saveGeenVoorkeur(o.id, gid);
        }}
      />
    );
  };

  // Optional add-ons: shown on a SELECTED offering with addonPolicyCode "optional" that actually has
  // add-ons. Each add-on is an EXPLICIT Ja / Nee choice (a clean two-option segmented control), and
  // it starts UNANSWERED, no default. The offering is not fully answered until EVERY add-on has a
  // Ja or Nee (folds into completeness via offeringFullyAnswered). An unanswered add-on shows a soft
  // amber hint. Each choice autosaves through setAddonAnswer.
  const renderAddons = (o: Offering) => {
    if (o.addonPolicyCode !== "optional") return null;
    const list = addonsByOffering.get(o.id);
    if (!list || list.length === 0) return null;
    const selId = selectionByOffering.get(o.id);
    if (!selId) return null;
    // Add-on answers are PER (selection, group): read + write the ACTIVE GROUP's answers only.
    const gid = activeGroup.id;
    const answers = addonAnswersBySelGroup.get(`${selId}:${gid}`) ?? new Map<string, boolean>();
    const anyUnanswered = list.some((a) => !answers.has(a.addonCode));
    return (
      <div className={cn("mt-2 rounded-md border px-3 py-2.5", anyUnanswered ? "border-amber-300 bg-amber-50/60" : "border-primary/30 bg-primary/[0.04]")}>
        <p className="text-xs font-semibold text-foreground">Extra&apos;s</p>
        <p className="mt-0.5 text-xs text-muted-foreground">Geef per extra aan of u die erbij wilt.</p>
        <div className="mt-2 overflow-hidden rounded-md border border-input bg-background">
          {list.map((a, i) => {
            const answer = answers.get(a.addonCode); // true = Ja, false = Nee, undefined = onbeantwoord
            return (
              <div
                key={a.addonCode}
                className={cn(
                  "flex items-start justify-between gap-3 px-3 py-2 text-sm",
                  i > 0 && "border-input border-t",
                )}
              >
                <span className="min-w-0">
                  <span className="font-medium text-foreground">{a.label}</span>
                  {a.description && <span className="block text-xs leading-5 text-muted-foreground">{a.description}</span>}
                </span>
                <JaNeeControl
                  value={answer}
                  disabled={saving}
                  onChange={(wanted) => setAddonAnswer(o.id, gid, a.addonCode, wanted)}
                />
              </div>
            );
          })}
        </div>
        {anyUnanswered && <p className="mt-1.5 text-xs font-medium text-amber-700">Beantwoord elke extra (ja of nee) om deze groep af te ronden.</p>}
      </div>
    );
  };

  // A KEUZE choice set is a VISUAL grouping of its members as full cards with the naive "kies 1"
  // pick (pickChoiceMember, passed as choiceCtx). Vaste are no longer rendered through choice sets:
  // they live in the unified vast block and their per-leerjaar exclusivity is GLOBAL across the
  // group's eligible vaste, so this helper is keuze-only now.
  const renderChoiceSet = (cs: ChoiceSet, memberIds: number[]) => (
    // BORDERLESS: the "Keuzeprogramma: één keuze" heading above already frames this as a "kies 1"
    // reeks, so the set needs no bordered box. We keep the set's own title as a light sub-label
    // (useful when a group has more than one keuzereeks) and render its members as borderless
    // divided cards, matching the rest of the section.
    <div key={cs.id} className="space-y-2">
      <p className="text-xs font-medium text-muted-foreground">{cs.title}</p>
      <div className="divide-y divide-border">
        {memberIds.map((id) => { const o = offeringById.get(id); return o ? renderCard(o, { memberIds }) : null; })}
      </div>
    </div>
  );

  return (
    <div className="space-y-5">
      <p className="pb-3 text-sm leading-6 text-muted-foreground">
        {mode === "technohub"
          ? "Kies per groep de TechnoHub-workshops waarvoor u interesse heeft. KUVO geeft uw interesse door aan TechnoHub Woerden, die de workshops zelf inplant."
          : "Kies het programma per groep. Loop de groepen langs en bevestig het vaste aanbod of kies keuzeprogramma's."}
      </p>

      {/* TechnoHub section: the "max 2 gratis" rule sits at the TOP, with a "Meer informatie"
          trigger that opens the shared TechKwadraat info dialog. The dynamic per-card "Eigen
          bijdrage" badge handles the "u zit er nu boven" case once the school passes 2 workshops. */}
      {mode === "technohub" && (
        <Notice
          tone="info"
          icon={<InfoIcon />}
          title="Maximaal 2 gratis TechnoHub-workshops via KUVO"
          action={
            <TechKwadraatInfoDialog
              trigger={
                <Button size="sm" variant="outline">
                  <InfoIcon className="mr-1.5 size-3.5" />
                  Meer informatie
                </Button>
              }
            />
          }
        >
          Een school mag maximaal 2 TechnoHub-workshops gratis afnemen via KUVO. Wilt u er meer? Dan
          vraagt dat een eigen bijdrage uit het TechKwadraat-budget.
        </Notice>
      )}

      <div>
        <h3 className="text-base font-semibold text-foreground">{activeGroup.displayName}</h3>
        <p className="text-xs text-muted-foreground">Leerjaren: {ylLabel}</p>
      </div>

      {eligible.length === 0 ? (
        <Notice tone="info" icon={<AlertCircleIcon />} title="Geen aanbod voor deze groep">
          Voor deze groep is er dit jaar geen vast- of keuzeprogramma.
        </Notice>
      ) : (
        // Three clearly headed blocks, always in this order: vast -> keuze "één keuze" -> keuze.
        // Cards are borderless (see renderCard), so the subheadings plus spacing (and a subtle
        // divider between cards in a block) carry the structure that the old bordered boxes did.
        <div className="space-y-6">
          {/* 1. VAST block: ALL eligible vaste voorstellingen of this group, under one heading
                (choice-set membership is no longer a visual unit; the per-leerjaar exclusivity is
                GLOBAL across this whole set). The "geen vast" decision stays under the block.
                (Empty in technohub mode — there is no vast there.) */}
          {hasVastAanbod && (
            <div className="space-y-3">
              <div>
                <h4 className="text-sm font-semibold text-foreground">Vast programma</h4>
                <p className="mt-0.5 text-xs leading-5 text-muted-foreground">
                  {isCombi ? "Kies per leerjaar van deze groep een vaste voorstelling." : "Kies een vaste voorstelling voor deze groep."}
                  {anyVastStackable && " Sommige voorstellingen mogen ook samen."}
                </p>
              </div>
              <div className="divide-y divide-border">{vast.map((o) => renderCard(o))}</div>
              {(isGeenVast(activeGroup.id) || !anyVastChosen) && (
                <label className={`flex items-center gap-2.5 rounded-lg border border-border bg-muted/30 px-3 py-2.5 text-sm ${saving ? "cursor-default opacity-60" : "cursor-pointer"}`}>
                  <input type="checkbox" checked={isGeenVast(activeGroup.id)} disabled={saving} onChange={() => toggleGeenVast(activeGroup.id)} className="size-4 accent-primary" />
                  <span className="text-foreground">Deze groep doet dit jaar niet mee aan het vaste programma.</span>
                </label>
              )}
            </div>
          )}

          {/* 2. KEUZE "kies 1" choice sets, under their own top-level keuze heading. Each set is a
                "kies 1" grouping; its members render as borderless divided cards. */}
          {keuzeSets.length > 0 && (
            <div className="space-y-3">
              <div>
                <h4 className="text-sm font-semibold text-foreground">Keuzeprogramma: één keuze</h4>
                <p className="mt-0.5 text-xs leading-5 text-muted-foreground">Kies per groep één programma uit deze reeks.</p>
              </div>
              {keuzeSets.map((x) => renderChoiceSet(x.cs, x.memberIds))}
            </div>
          )}

          {/* 3. Standalone KEUZE cards, under their own heading. */}
          {keuze.length > 0 && (
            <div className="space-y-3">
              <div>
                <h4 className="text-sm font-semibold text-foreground">Keuzeprogramma</h4>
                <p className="mt-0.5 text-xs leading-5 text-muted-foreground">
                  {mode === "technohub" ? "Kies de workshops waarvoor deze groep interesse heeft." : "Kies één of meer programma's voor deze groep."}
                </p>
              </div>
              <div className="divide-y divide-border">{keuze.map((o) => renderCard(o))}</div>
            </div>
          )}

          {/* 4. The keuze opt-out, last. */}
          {hasKeuzeAanbod && (isSectionOptedOut(activeGroup.id) || !anyKeuzeChosen) && (
            <label className={`flex items-center gap-2.5 rounded-lg border border-border bg-muted/30 px-3 py-2.5 text-sm ${saving ? "cursor-default opacity-60" : "cursor-pointer"}`}>
              <input type="checkbox" checked={isSectionOptedOut(activeGroup.id)} disabled={saving} onChange={() => toggleSectionOptOut(activeGroup.id)} className="size-4 accent-primary" />
              <span className="text-foreground">{mode === "technohub" ? "Deze groep doet dit jaar niet mee aan de TechnoHub-workshops." : "Deze groep doet dit jaar niet mee aan het keuzeprogramma."}</span>
            </label>
          )}
        </div>
      )}

      {/* Progress is no longer blocked here: completeness + confirmation live in the
          Controleren step. This button just advances to the next step. */}
      <div className="pt-5">
        <Button size="lg" onClick={primaryAction.onClick}>{primaryAction.label}</Button>
      </div>
    </div>
  );
}

// Voorkeursperiode control: a COMPACT MULTI-select GRID of the offering's own date-blocks PLUS a
// "Geen voorkeur" option. The school must ANSWER it: pick ONE OR MORE periods (`value` = the chosen
// block ids as strings) OR tick "Geen voorkeur" (`geenVoorkeur`). The two are MUTUALLY EXCLUSIVE:
// ticking a period clears "Geen voorkeur", ticking "Geen voorkeur" clears all periods (the parent's
// handlers enforce this through the /voorkeur save). It is ALWAYS required, so until either is
// chosen the control shows the amber "kies een periode of Geen voorkeur" hint and the offering stays
// unresolved. Persists on each toggle via onToggle(blockId) / onGeenVoorkeur(). The periods render as
// a ~3-column grid so it stays compact; "Geen voorkeur" sits on its own row below.
function VoorkeurControl({
  value, geenVoorkeur, options, onToggle, onGeenVoorkeur, disabled = false,
}: {
  value: string[];
  geenVoorkeur: boolean;
  options: { value: string; label: string }[];
  onToggle: (blockId: string) => void;
  onGeenVoorkeur: () => void;
  disabled?: boolean;
}) {
  const labelId = useId();
  const chosen = new Set(value);
  // Unanswered = no periods chosen AND not "Geen voorkeur". The voorkeursperiode is always required.
  const missing = chosen.size === 0 && !geenVoorkeur;

  return (
    <div className={cn("mt-2 rounded-md border px-3 py-2.5", missing ? "border-amber-300 bg-amber-50/60" : "border-primary/30 bg-primary/[0.04]")}>
      <p id={labelId} className="text-xs font-semibold text-foreground">
        Voorkeursperiode<span className="font-normal text-amber-700"> (verplicht)</span>
      </p>
      <p className="mt-0.5 text-xs text-muted-foreground">In welke periode(s) ziet u dit het liefst plaatsvinden? U kunt er meerdere kiezen, of kies "Geen voorkeur" als het u niet uitmaakt. Selecteer een periode om die te kiezen of uit te zetten.</p>
      {/* COMPACT GRID: ~3 columns of toggle-checkbox cells (drops to 2/1 column on narrow viewports). */}
      <div role="group" aria-labelledby={labelId} className="mt-2 grid grid-cols-2 gap-1.5 sm:grid-cols-3">
        {options.map((opt) => {
          const on = chosen.has(opt.value);
          return (
            <label
              key={opt.value}
              className={cn(
                "flex items-start gap-2 rounded-md border px-2.5 py-2 text-xs transition-colors",
                disabled ? "cursor-default opacity-60" : "cursor-pointer",
                on ? "border-primary bg-primary/[0.08] text-foreground" : "border-input bg-background text-foreground hover:border-primary/40 hover:bg-accent/40",
              )}
            >
              <Checkbox checked={on} disabled={disabled} aria-invalid={missing || undefined} onCheckedChange={() => onToggle(opt.value)} className="mt-0.5" />
              <span className="min-w-0 leading-snug">{opt.label}</span>
            </label>
          );
        })}
      </div>
      {/* "Geen voorkeur" is the deliberate "het maakt niet uit"-answer; mutually exclusive with the
          periods above. Ticking it clears the periods; ticking a period clears it. */}
      <label
        className={cn(
          "mt-1.5 flex items-center gap-2 rounded-md border px-2.5 py-2 text-xs transition-colors",
          disabled ? "cursor-default opacity-60" : "cursor-pointer",
          geenVoorkeur ? "border-primary bg-primary/[0.08] text-foreground" : "border-input bg-background text-foreground hover:border-primary/40 hover:bg-accent/40",
        )}
      >
        <Checkbox checked={geenVoorkeur} disabled={disabled} aria-invalid={missing || undefined} onCheckedChange={() => onGeenVoorkeur()} />
        <span className="leading-snug">Geen voorkeur, het maakt ons niet uit wanneer</span>
      </label>
      {missing && <p className="mt-1.5 text-xs font-medium text-amber-700">Kies minimaal één voorkeursperiode, of kies "Geen voorkeur", om deze groep af te ronden.</p>}
    </div>
  );
}

// A clean two-option Ja / Nee segmented control for ONE add-on answer. `value` is the current
// answer: true = Ja, false = Nee, undefined = nog onbeantwoord (no segment highlighted). Picking a
// segment calls onChange(wanted) and autosaves. Compact pill shape so it sits tidily on the add-on row.
function JaNeeControl({
  value, onChange, disabled = false,
}: {
  value: boolean | undefined;
  onChange: (wanted: boolean) => void;
  disabled?: boolean;
}) {
  const options: { wanted: boolean; label: string }[] = [
    { wanted: true, label: "Ja" },
    { wanted: false, label: "Nee" },
  ];
  return (
    <div role="group" aria-label="Ja of nee" className="inline-flex shrink-0 overflow-hidden rounded-md border border-input">
      {options.map((opt, i) => {
        const on = value === opt.wanted;
        return (
          <button
            key={opt.label}
            type="button"
            disabled={disabled}
            aria-pressed={on}
            onClick={() => onChange(opt.wanted)}
            className={cn(
              "px-3 py-1 text-xs font-medium transition-colors disabled:pointer-events-none disabled:opacity-50",
              i > 0 && "border-input border-l",
              on ? "bg-primary text-primary-foreground" : "bg-background text-foreground hover:bg-accent/60",
            )}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

// READ-ONLY details dialog opened by an offering card's "Meer informatie" link. There is NO editing
// here (the card owns all the choices), so the dialog has no confirm/save, only a "Sluiten" button.
// It shows the large image, the title + provider, the stacked stats (Wanneer / periode, Duur,
// Locatie, Voor leerjaren) and the FULL description (longDescription, falling back to the short one).
// `voorLeerjaren` is precomputed by the card (it depends on the active group's eligible leerjaren),
// passed in as a ready string or null; the rest comes straight off the programme.
export function OfferingDetailsDialog({
  programme, providerName, voorLeerjaren, voorkeursperioden = [], addons = [], trigger,
}: {
  programme: Programme;
  providerName: string | null;
  voorLeerjaren: string | null;
  voorkeursperioden?: string[];
  addons?: string[];
  trigger: React.ReactNode;
}) {
  const p = programme;
  const stats: { icon: typeof CalendarIcon; label: string; value: string }[] = [];
  if (p.periodText) stats.push({ icon: CalendarIcon, label: "Wanneer", value: p.periodText });
  if (p.durationText) stats.push({ icon: ClockIcon, label: "Duur", value: p.durationText });
  if (p.locationText) stats.push({ icon: MapPinIcon, label: "Locatie", value: p.locationText });
  if (voorLeerjaren) stats.push({ icon: UsersIcon, label: "Voor leerjaren", value: voorLeerjaren });
  const description = p.longDescription || p.shortDescription;
  return (
    <Dialog>
      <DialogTrigger render={trigger as React.ReactElement} />
      <DialogPopup className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{p.title}</DialogTitle>
          {providerName && <DialogDescription>{providerName}</DialogDescription>}
        </DialogHeader>
        <DialogPanel>
          <div className="space-y-4">
            {p.imagePath && (
              <img src={p.imagePath} alt="" className="h-48 w-full rounded-xl object-cover" loading="lazy" />
            )}
            {stats.length > 0 && (
              <dl className="grid gap-y-2.5 rounded-xl border border-border bg-muted/30 px-4 py-3 text-sm">
                {stats.map((s) => {
                  const Icon = s.icon;
                  return (
                    <div key={s.label} className="flex items-start gap-3">
                      <Icon aria-hidden size={16} className="mt-0.5 shrink-0 text-muted-foreground/70" />
                      <dt className="w-28 shrink-0 font-medium text-muted-foreground">{s.label}</dt>
                      <dd className="min-w-0 text-foreground">{s.value}</dd>
                    </div>
                  );
                })}
              </dl>
            )}
            {/* Informative only: which voorkeursperioden + add-ons this activity offers, as compact chips. */}
            {voorkeursperioden.length > 0 && (
              <div>
                <p className="mb-1.5 text-xs font-medium text-muted-foreground">Voorkeursperioden</p>
                <div className="flex flex-wrap gap-1.5">
                  {voorkeursperioden.map((v) => (
                    <span key={v} className="rounded-full border border-border bg-background px-2.5 py-1 text-xs text-foreground">{v}</span>
                  ))}
                </div>
              </div>
            )}
            {addons.length > 0 && (
              <div>
                <p className="mb-1.5 text-xs font-medium text-muted-foreground">Add-ons</p>
                <div className="flex flex-wrap gap-1.5">
                  {addons.map((a) => (
                    <span key={a} className="rounded-full border border-border bg-background px-2.5 py-1 text-xs text-foreground">{a}</span>
                  ))}
                </div>
              </div>
            )}
            {description && <p className="whitespace-pre-line text-sm leading-6 text-foreground">{description}</p>}
          </div>
        </DialogPanel>
        <DialogFooter>
          <DialogClose render={<Button variant="outline">Sluiten</Button>} />
        </DialogFooter>
      </DialogPopup>
    </Dialog>
  );
}

// Reusable info dialog explaining the TechKwadraat / TechnoHub scheme and the "max 2 gratis
// workshops" rule. Triggered by a small "Meer informatie" button / info icon so the cards stay
// clean. Copy is warm and graceful, no em dashes (project rule). The `trigger` prop lets each
// call site supply its own opener (a "Meer informatie" link, an info-icon button on a card, etc.).
export function TechKwadraatInfoDialog({ trigger }: { trigger: React.ReactNode }) {
  return (
    <Dialog>
      <DialogTrigger render={trigger as React.ReactElement} />
      <DialogPopup className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Over TechKwadraat en de TechnoHub</DialogTitle>
          <DialogDescription>Wat het inhoudt en hoe de eigen bijdrage werkt.</DialogDescription>
        </DialogHeader>
        <DialogPanel>
          <div className="space-y-4 text-sm leading-6 text-foreground">
            <p>
              KUVO sluit met haar techniekaanbod aan bij de uitgangspunten van TechKwadraat. Daarin
              staan duurzaamheid en recycling centraal, en werken kinderen met moderne apparatuur
              zoals de lasersnijder en de 3D-printer in het maakproces.
            </p>
            <div>
              <p className="font-medium text-foreground">Wat hoort er allemaal bij?</p>
              <ul className="mt-1.5 list-disc space-y-1 pl-5 text-muted-foreground">
                <li>Werken met moderne apparatuur, zoals de lasersnijder en de 3D-printer, in het maakproces.</li>
                <li>Een bezoek aan een KunstLab in het voortgezet onderwijs.</li>
                <li>Nieuw dit jaar: een bezoek aan het gemaal, in het kader van techniek en waterbeheer.</li>
                <li>Een structurele samenwerking met de TechnoHub voor onderzoekend en ontwerpend leren.</li>
              </ul>
            </div>
            <div className="rounded-lg border border-amber-500/40 bg-amber-50/60 px-3 py-2.5">
              <p className="font-medium text-amber-900">De eigen bijdrage</p>
              <p className="mt-1 text-[13px] leading-snug text-amber-900/90">
                Een school mag maximaal 2 TechnoHub-workshops gratis afnemen via KUVO. Wilt u er meer,
                dan vraagt dat een eigen bijdrage uit het TechKwadraat-budget, gecombineerd met matching
                vanuit KUVO en de TechnoHub.
              </p>
            </div>
            <p className="text-muted-foreground">
              Maatwerk en projectweken zijn daarnaast rechtstreeks boekbaar via{" "}
              <a href="mailto:anja.klever@technohub.nl" className="font-medium text-primary underline-offset-2 hover:underline">
                anja.klever@technohub.nl
              </a>.
            </p>
          </div>
        </DialogPanel>
        <DialogFooter>
          <DialogClose render={<Button variant="outline">Sluiten</Button>} />
        </DialogFooter>
      </DialogPopup>
    </Dialog>
  );
}

// Inline INFO alert (the Origin UI Kit alert-info-neutral look: a neutral bordered row with a blue
// InfoIcon) used to explain the TechKwadraat/KUVO budgeting on a card or at the top of a section,
// with a "Meer informatie" link (the shared TechKwadraatInfoDialog, rendered as the link-with-arrow
// from the alert-warning-link-colored example). The message differs per context (the 3D printen /
// Lasersnijder activities vs the TechnoHub workshops), so it is passed in as children.
export function TechKwadraatInfoNotice({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-md border px-4 py-3">
      <div className="flex gap-3">
        <InfoIcon aria-hidden className="mt-0.5 shrink-0 text-blue-500" size={16} />
        <div className="flex grow flex-col gap-1.5 text-sm sm:flex-row sm:items-start sm:justify-between sm:gap-3">
          <p>{children}</p>
          <TechKwadraatInfoDialog
            trigger={
              <button type="button" className="group cursor-pointer whitespace-nowrap font-medium hover:underline">
                Meer informatie
                <ArrowRightIcon aria-hidden className="-mt-0.5 ms-1 inline-flex opacity-60 transition-transform group-hover:translate-x-0.5" size={16} />
              </button>
            }
          />
        </div>
      </div>
    </div>
  );
}

// The card notice for the static techKwadraat offerings (3D printen, Lasersnijder in vast_keuze mode).
export function TechKwadraatNotice() {
  return <TechKwadraatInfoNotice>Deze activiteit valt onder het aanbod van TechKwadraat en KUVO.</TechKwadraatInfoNotice>;
}
